import React, { useMemo } from 'react';
import { Box, Typography, Chip, Grid, Divider } from '@material-ui/core';
import { green, blue, red, amber } from '@material-ui/core/colors';
import { useQueryCustom } from 'hooks';
import { IVR_CALL_DETAIL_BY_SID } from 'gql';
import {
  IvrCallDetailBySidQuery,
  IvrCallDetailBySidQueryVariables,
} from 'types.d';
import IvrRecording from './IvrRecording';

type Props = { callSid: string };

export const CallStatusTab: React.FC<Props> = ({ callSid }) => {
  const { data, loading } = useQueryCustom<
    IvrCallDetailBySidQuery,
    IvrCallDetailBySidQueryVariables
  >({
    api: IVR_CALL_DETAIL_BY_SID,
    variables: { params: { callSid } },
    fetchPolicy: 'network-only',
  });

  const ctx = useMemo(() => data?.ivrCallDetailBySid?.context, [data]);

  const statusLabel = (raw?: string) => {
    if (!raw) return 'Unknown';
    return raw
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const statusColor = (raw?: string) => {
    if (!raw) return { backgroundColor: '#e0e0e0', color: '#000' };
    switch (raw) {
      case 'COMPLETED':
        return { backgroundColor: green[600], color: '#fff' };
      case 'IN_PROGRESS':
      case 'ANSWERED':
      case 'RINGING':
      case 'INITIATED':
        return { backgroundColor: blue[600], color: '#fff' };
      case 'NO_ANSWER':
        return { backgroundColor: amber[700], color: '#000' };
      case 'FAILED':
      case 'BUSY':
      case 'CANCELED':
        return { backgroundColor: red[600], color: '#fff' };
      default:
        return { backgroundColor: '#e0e0e0', color: '#000' };
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (!ctx) return <Typography>No call status found.</Typography>;

  return (
    <Box mt={2} display="flex" flexDirection="column">
      {/* Status / Created / Updated */}
      <Box style={{ border: '1px solid #eee', borderRadius: 6, padding: 12 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary" variant="caption">Status</Typography>
            <Box mt={0.5}>
              <Chip
                size="small"
                label={statusLabel(ctx.status as any)}
                style={statusColor(ctx.status as any)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary" variant="caption">Created</Typography>
            <Typography variant="body2">
              {ctx.createdAt ? new Date(ctx.createdAt as any).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary" variant="caption">Updated</Typography>
            <Typography variant="body2">
              {ctx.updatedAt ? new Date(ctx.updatedAt as any).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Recording */}
      <Box mt={2} style={{ border: '1px solid #eee', borderRadius: 6, padding: 12 }}>
        <Typography color="textSecondary" variant="caption">Recording</Typography>
        <Box mt={0.5} display="flex" alignItems="center" justifyContent="space-between">
          {ctx.recordingUrl ? (
            <>
              <IvrRecording
                recordingUrl={ctx.recordingUrl}
                callSid={ctx.callSid}
                width={320}
              />
              {typeof ctx.recordingDuration === 'number' && (
                <Chip size="small" label={`${ctx.recordingDuration}s`} />
              )}
            </>
          ) : (
            <Typography variant="body2">N/A</Typography>
          )}
        </Box>
      </Box>

      {/* Details */}
      <Box mt={2} style={{ border: '1px solid #eee', borderRadius: 6, padding: 12 }}>
        <Typography color="textSecondary" variant="caption">Details</Typography>
        <Box mt={0.5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography color="textSecondary" variant="caption">Call SID</Typography>
              <Typography variant="body2">{ctx.callSid}</Typography>
            </Grid>
            {ctx.orgName && (
              <Grid item xs={12} sm={4}>
                <Typography color="textSecondary" variant="caption">Organization</Typography>
                <Typography variant="body2">{ctx.orgName}</Typography>
              </Grid>
            )}
            {typeof ctx.recordingSid === 'string' && (
              <Grid item xs={12} sm={4}>
                <Typography color="textSecondary" variant="caption">Recording SID</Typography>
                <Typography variant="body2">{ctx.recordingSid}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      {/* Retry Info */}
      <Box mt={2} style={{ border: '1px solid #eee', borderRadius: 6, padding: 12 }}>
        <Typography color="textSecondary" variant="caption">Retry Info</Typography>
        <Box mt={0.5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Box display="flex" alignItems="center">
                <Typography color="textSecondary" variant="caption">Max Retries</Typography>
                {typeof ctx.maxRetries === 'number' ? (
                  <Chip size="small" label={String(ctx.maxRetries)} style={{ marginLeft: 8 }} />
                ) : (
                  <Typography variant="body2" style={{ marginLeft: 8 }}>N/A</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box display="flex" alignItems="center">
                <Typography color="textSecondary" variant="caption">Retries Remaining</Typography>
                {typeof ctx.retriesRemaining === 'number' ? (
                  <Chip size="small" label={String(ctx.retriesRemaining)} style={{ marginLeft: 8 }} />
                ) : (
                  <Typography variant="body2" style={{ marginLeft: 8 }}>N/A</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box display="flex" alignItems="center">
                <Typography color="textSecondary" variant="caption">Retry Interval (min)</Typography>
                {typeof ctx.retryIntervalMinutes === 'number' ? (
                  <Chip size="small" label={String(ctx.retryIntervalMinutes)} style={{ marginLeft: 8 }} />
                ) : (
                  <Typography variant="body2" style={{ marginLeft: 8 }}>N/A</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box display="flex" alignItems="center">
                <Typography color="textSecondary" variant="caption">Next Retry At</Typography>
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  {ctx.nextRetryAt ? new Date(ctx.nextRetryAt as any).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {(Array.isArray(ctx.attempts) || typeof ctx.attempts === 'number') && (
        <Box mt={2}>
          <Typography color="textSecondary" variant="caption">Attempts</Typography>
          {Array.isArray(ctx.attempts) ? (
            <Box mt={0.5}>
              <Grid container spacing={1}>
                {ctx.attempts.map((a: any, idx: number) => (
                  <Grid key={idx} item xs={12}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{ padding: '8px 12px', border: '1px solid #eee', borderRadius: 6 }}
                    >
                      <Typography variant="body2">
                        {a?.at ? new Date(a.at).toLocaleString() : `Attempt #${idx + 1}`}
                      </Typography>
                      {a?.status && (
                        <Chip
                          size="small"
                          label={statusLabel(String(a.status))}
                          style={statusColor(String(a.status))}
                        />
                      )}
                      {a?.callSid ? (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ marginLeft: 12 }}
                          noWrap
                        >
                          {a.callSid}
                        </Typography>
                      ) : (
                        <span />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Typography variant="body2">{ctx.attempts}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(CallStatusTab);
